import { m } from '@pricedrop/i18n';
import { useLocation, useNavigate } from '@tanstack/react-router';
import Search from 'lucide-react/dist/esm/icons/search';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useEffect, useRef, useState } from 'react';

export function SearchInput() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [{ query }, setParams] = useQueryStates(
    {
      query: parseAsString,
      page: parseAsInteger.withDefault(1),
    },
    {
      history: 'replace',
      clearOnDefault: true,
    },
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inputValue, setInputValue] = useState(query ?? '');
  const isOnItemsPage = pathname === '/items' || pathname === '/items/';

  const clearDebounceTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const applyQueryToItemsParams = (value: string) => {
    const trimmed = value.trim();
    void setParams({ query: trimmed === '' ? null : trimmed, page: 1 });
  };

  const navigateToItems = (value: string) => {
    const trimmed = value.trim();
    void navigate({
      to: '/items',
      search: {
        query: trimmed === '' ? undefined : trimmed,
        page: 1,
      },
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (!isOnItemsPage) {
      navigateToItems(value);
      return;
    }

    clearDebounceTimer();
    timeoutRef.current = setTimeout(() => {
      applyQueryToItemsParams(value);
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;

    clearDebounceTimer();

    if (isOnItemsPage) {
      applyQueryToItemsParams(inputValue);
      return;
    }

    navigateToItems(inputValue);
  };

  useEffect(() => {
    setInputValue(query ?? '');
  }, [query]);

  useEffect(() => {
    if (pathname) {
      clearDebounceTimer();
    }

    return () => {
      clearDebounceTimer();
    };
  }, [pathname, clearDebounceTimer]);

  return (
    <div className="relative me-3 flex-1 md:grow-0">
      <Search className="text-muted-foreground absolute top-2.5 start-2.5 h-5 w-5" />
      <input
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={inputValue}
        type="search"
        placeholder={m.common_search()}
        className="bg-background h-10 w-full rounded-md border ps-8 pe-3 text-sm md:w-[120px] lg:w-[200px] xl:w-[250px]"
      />
    </div>
  );
}
